import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle as CheckCircle2, Handshake } from "@phosphor-icons/react";
import { toast } from 'sonner';
import { SiteFooter } from '../components/SiteFooter';
import { SiteNav } from '../components/SiteNav';

const API = process.env.REACT_APP_BACKEND_URL;

const AUDIENCES = [
  'Bookkeepers', 'Tax pros', 'Banks, credit unions & fintechs', 'Freelancer coaches',
  'Coworking spaces', 'ADHD/neurodivergent work coaches'
];

const WAYS = [
  'Refer clients or community members',
  'Bundle Payday Relay into a program or workshop',
  'Host a guest training or live demo',
  'Collaborate on content about unstructured income',
  'Explore affiliate or revenue-share options later'
];

export default function PartnershipsPage() {
  const [form, setForm] = useState({ name: '', email: '', organization: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.name.trim() || !form.email.includes('@') || !form.message.trim()) {
      toast.error('Please add your name, email, and a short note.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/partnerships/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data?.detail?.[0]?.msg || data?.detail || "Couldn't send, please try again.");
        return;
      }
      setSent(true);
    } catch {
      toast.error('Network hiccup. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#FDFBF7' }} data-testid="partnerships-page">
      <SiteNav />

      {/* Hero */}
      <section className="pt-14 sm:pt-20 pb-10 px-4 sm:px-6 lg:px-8" style={{ background: '#2A1E5C' }}>
        <div className="max-w-3xl mx-auto text-center pb-8">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-6" style={{ background: 'rgba(245, 203, 92, 0.2)', color: '#F5CB5C' }}>
            <Handshake className="w-4 h-4" /> Partnerships
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight leading-tight mb-5 text-white">
            Partner With Payday Relay<sup className="text-[0.45em] align-super"></sup>
          </h1>
          <p className="font-heading text-lg sm:text-xl" style={{ color: '#F5CB5C' }}>
            Help 1099 professionals build structure around unstructured income.
          </p>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <p className="font-body text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: '#DE3C4B' }}>
            WHO IT'S FOR
          </p>
          <div className="flex flex-wrap gap-2.5 mb-12" data-testid="partner-audiences">
            {AUDIENCES.map((a) => (
              <span key={a} className="px-3.5 py-1.5 rounded-full font-body text-sm font-medium" style={{ background: '#FAF1D8', color: '#2A1E5C', border: '1px solid rgba(42,30,92,0.12)' }}>
                {a}
              </span>
            ))}
          </div>

          <p className="font-body text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: '#DE3C4B' }}>
            WHY IT MATTERS
          </p>
          <p className="font-body text-base sm:text-lg leading-relaxed mb-12" style={{ color: '#5D5A6B' }}>
            The work economy changed faster than the support systems around it. Payday Relay helps people with available work but missing structure know what this week needs from them, when they can stop, and how to pass money forward to taxes, PTO, debt, savings, and take-home pay.
          </p>

          <p className="font-body text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: '#DE3C4B' }}>
            WAYS TO PARTNER
          </p>
          <ul className="space-y-3 mb-6" data-testid="partner-ways">
            {WAYS.map((w) => (
              <li key={w} className="flex items-start gap-3 font-body text-base" style={{ color: '#2A1E5C' }}>
                <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#4CAF50' }} />
                {w}
              </li>
            ))}
          </ul>
          <p className="font-body text-sm italic" style={{ color: '#8B7E6A' }}>
            Partnerships are exploratory and founder-led right now, we'll shape the details together.
          </p>
        </div>
      </section>

      {/* Contact form */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8" style={{ background: '#FAF1D8' }}>
        <div className="max-w-xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-2 text-center" style={{ color: '#2A1E5C' }}>
            Start a Partnership Conversation
          </h2>
          <p className="font-body text-sm text-center mb-8" style={{ color: '#5D5A6B' }}>
            Tell us a little about you and what you have in mind.
          </p>

          {sent ? (
            <div className="p-8 rounded-3xl text-center" style={{ background: '#FFFFFF', boxShadow: '0 6px 24px rgba(42,30,92,0.08)' }} data-testid="partner-success">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(76, 175, 80, 0.15)', color: '#4CAF50' }}>
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-2" style={{ color: '#2A1E5C' }}>
                Message sent 🏁
              </h3>
              <p className="font-body text-sm" style={{ color: '#5D5A6B' }}>
                Thanks for reaching out, our team reads every partnership note and will reply from partnerships@paydayrelay.com.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 sm:p-8 rounded-3xl space-y-4" style={{ background: '#FFFFFF', boxShadow: '0 6px 24px rgba(42,30,92,0.08)' }} data-testid="partner-form">
              <div>
                <label className="block font-body text-xs font-bold mb-1 uppercase tracking-wider" style={{ color: '#2A1E5C' }}>Name</label>
                <input type="text" required value={form.name} onChange={set('name')} placeholder="Your name"
                  className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none"
                  style={{ background: '#FDFBF7', border: '1px solid rgba(42, 30, 92, 0.2)', color: '#2A1E5C' }}
                  data-testid="partner-name-input" />
              </div>
              <div>
                <label className="block font-body text-xs font-bold mb-1 uppercase tracking-wider" style={{ color: '#2A1E5C' }}>Email</label>
                <input type="email" required value={form.email} onChange={set('email')} placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none"
                  style={{ background: '#FDFBF7', border: '1px solid rgba(42, 30, 92, 0.2)', color: '#2A1E5C' }}
                  data-testid="partner-email-input" />
              </div>
              <div>
                <label className="block font-body text-xs font-bold mb-1 uppercase tracking-wider" style={{ color: '#2A1E5C' }}>Organization / community <span className="normal-case font-normal">(optional)</span></label>
                <input type="text" value={form.organization} onChange={set('organization')} placeholder="Where do you support independent earners?"
                  className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none"
                  style={{ background: '#FDFBF7', border: '1px solid rgba(42, 30, 92, 0.2)', color: '#2A1E5C' }}
                  data-testid="partner-org-input" />
              </div>
              <div>
                <label className="block font-body text-xs font-bold mb-1 uppercase tracking-wider" style={{ color: '#2A1E5C' }}>What do you have in mind?</label>
                <textarea required rows={5} value={form.message} onChange={set('message')} placeholder="Referrals, a workshop bundle, a live demo, content collab…"
                  className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none resize-y"
                  style={{ background: '#FDFBF7', border: '1px solid rgba(42, 30, 92, 0.2)', color: '#2A1E5C' }}
                  data-testid="partner-message-input" />
              </div>
              <button type="submit" disabled={busy}
                className="w-full py-3.5 rounded-full font-body font-bold text-sm transition-transform hover:scale-[1.01] shadow-md flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ background: '#2A1E5C', color: '#FFFFFF' }}
                data-testid="partner-submit">
                {busy ? 'Sending…' : (<>Start a Partnership Conversation <ArrowRight className="w-4 h-4" style={{ color: '#F5CB5C' }} /></>)}
              </button>
              <p className="font-body text-[11px] text-center" style={{ color: '#8B7E6A' }}>
                Goes straight to partnerships@paydayrelay.com
              </p>
            </form>
          )}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
